'use client';

import { ReactNode, useState, forwardRef } from 'react';
import Alert, { AlertColor } from '@mui/material/Alert';
import { SxProps, Theme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckIcon from '@mui/icons-material/Check';

interface Action {
	label: string;
	onClick: () => void;
	testId?: string;
	startIcon?: ReactNode;
	endIcon?: ReactNode;
}

interface NotificationAlertProps {
	showIcon?: boolean;
	severity?: AlertColor;
	sx?: SxProps<Theme>;
	id?: string;
	closable?: boolean;
	primaryAction?: Action;
	secondaryAction?: Action;
	children: ReactNode;
}

const NotificationAlert = forwardRef<HTMLDivElement, NotificationAlertProps>(function NotificationAlert(
	{ showIcon = true, severity = 'info', sx, id, closable = false, primaryAction, secondaryAction, children },
	ref,
) {
	const [open, setOpen] = useState(true);

	if (closable && !open) return null;

	const notificationSeverity: Record<AlertColor, { color: string; icon: ReactNode }> = {
		info: { color: 'blue.main', icon: <ReceiptLongIcon htmlColor="white" /> },
		success: { color: 'green.main', icon: <CheckIcon htmlColor="white" /> },
		warning: { color: 'yellow.medium', icon: <ErrorOutlineIcon htmlColor="white" /> },
		error: { color: 'red.main', icon: <WarningAmberIcon htmlColor="white" /> },
	};

	const getSeverityIcon = (severity: AlertColor) => {
		const { color, icon } = notificationSeverity[severity];
		return (
			<Box
				data-testid="alert-icon"
				sx={{
					backgroundColor: color,
					borderRadius: '50%',
					width: 40,
					height: 40,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					flexShrink: 0,
				}}
			>
				{icon}
			</Box>
		);
	};

	return (
		<Alert
			ref={ref}
			severity={severity}
			variant="outlined"
			icon={false}
			onClose={closable ? () => setOpen(false) : undefined}
			sx={{
				...sx,
				position: 'relative',
				alignItems: 'center',
				flexWrap: { md: 'nowrap', xs: 'wrap' },
				borderColor: notificationSeverity[severity].color,
			}}
			data-testid={id ?? 'notification-alert'}
			action={
				<>
					{primaryAction && (
						<Button
							color="primary"
							sx={{ whiteSpace: 'nowrap' }}
							variant="text"
							onClick={primaryAction.onClick}
							data-testid={primaryAction.testId}
							startIcon={primaryAction.startIcon}
							endIcon={primaryAction.endIcon}
						>
							{primaryAction.label}
						</Button>
					)}
					{secondaryAction && (
						<Button
							color="primary"
							sx={{ whiteSpace: 'nowrap' }}
							variant="text"
							onClick={secondaryAction.onClick}
							data-testid={secondaryAction.testId}
							startIcon={secondaryAction.startIcon}
							endIcon={secondaryAction.endIcon}
						>
							{secondaryAction.label}
						</Button>
					)}
					{closable && (
						<IconButton
							aria-label="close"
							size="small"
							sx={{ position: 'absolute', top: 0, right: 1, color: 'grey.dark' }}
							onClick={() => setOpen(false)}
						>
							<CloseIcon fontSize="inherit" />
						</IconButton>
					)}
				</>
			}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
				{showIcon ? getSeverityIcon(severity) : null}
				{children}
			</Box>
		</Alert>
	);
});

export default NotificationAlert;
